import { Link, useLocation } from "react-router-dom";

const ACTION_LABELS: Record<string, string> = {
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    view: 'View'
};

type Crumb = {
    label: string
    to?: string
}

type AdminTrailProps = {
    // Label for the section crumb, e.g. "Hosts".
    section: string
    // Route the section crumb links back to, e.g. "/admin/hosts".
    sectionPath: string
    // Crumb between Settings and the section, for nested sections such as the
    // individual integrations, e.g. { label: 'Integrations', to: '/admin/integrations' }.
    parent?: Crumb
    // Set for sections that address a record directly as /<id> with no action
    // segment, e.g. Licences.
    recordIdWithoutAction?: boolean
    // Name of the record being acted on, where the form itself tracks it. Takes
    // precedence over resolveEntityLabel. Only used once the URL carries an id, so
    // the create form ends at the action crumb.
    entityLabel?: string
    // Resolves the id in the URL to the name of the record being acted on. Return
    // undefined while the record is still loading and the crumb is left off.
    resolveEntityLabel?: (id: string) => string | undefined
}

// Breadcrumb trail for admin sections following the /admin/<section>/<action>/<id>
// route shape. Renders every crumb except the last as a link so the chevron
// separators (`.breadcrumbs a:after`) apply, and the last as a plain span. On a
// create/edit/delete form it appends the action and the record being acted on.
const AdminTrail = ({ section, sectionPath, parent, recordIdWithoutAction, entityLabel, resolveEntityLabel }: AdminTrailProps) => {
    const location = useLocation();
    // [<action>?, <id>?]
    const segments = location.pathname.replace(/\/+$/, '')
        .slice(sectionPath.length)
        .split('/')
        .filter(Boolean);
    let action: string | undefined = segments[0];
    let id: string | undefined = segments[1];

    if (recordIdWithoutAction && action && id === undefined && /^\d+$/.test(action)) {
        id = action;
        action = undefined;
    }

    const crumbs: Crumb[] = [{ label: 'Settings', to: '/admin' }];

    if (parent) {
        crumbs.push(parent);
    }

    if (action || id) {
        crumbs.push({ label: section, to: sectionPath });

        if (action) {
            crumbs.push({ label: ACTION_LABELS[action] ?? (action.charAt(0).toUpperCase() + action.slice(1)) });
        }

        const recordLabel = id ? (entityLabel || resolveEntityLabel?.(id)) : undefined;
        if (recordLabel) {
            crumbs.push({ label: recordLabel });
        }
    } else {
        crumbs.push({ label: section });
    }

    return (
        <>
            {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;
                if (isLast) {
                    return <span key={index}>{crumb.label}</span>;
                }
                // A crumb with no target is the current page (e.g. the action): render
                // it as a non-navigating link so it still gets a chevron separator.
                return crumb.to
                    ? <Link key={index} to={crumb.to}>{crumb.label}</Link>
                    : <Link key={index} to={location.pathname}
                            className="current-breadcrumb-link pe-none">{crumb.label}</Link>;
            })}
        </>
    );
};

export default AdminTrail;
